import {type CreateExtArgs, pointerUtils} from '@wix/document-manager-core'
import type {Pointer, Builder} from '@wix/document-services-types'
// eslint-disable-next-line wix-editor/no-internal-import
import {ComponentManifestBuilder, EditorElementBuilder} from '@wix/component-protocol/builder'
import {getRootAppWidgetByPage} from './blocksDataModel'
import {VIEW_MODES} from '../../constants/constants'
import type {FeaturesAPI, FeaturesExtensionAPI} from '../features/features'
import {MANIFEST_FEATURE_NAME} from './manifestUtils'
import {VariablesServiceFactory} from './variables/factory'
import type {VariantsExtensionAPI} from '../variants/variants'
import type {NicknameContextExtensionAPI} from '../nicknameContext'
import type {NicknamesAPI, NicknamesExtensionAPI} from '../nicknames'
import {WidgetRepository} from './repository/WidgetRepository'
import {ComponentRepository} from './repository/ComponentRepository'
import {ComponentDataRepository} from './repository/ComponentDataRepository'
import {RefComponentRepository} from './repository/RefComponentRepository'
import {ManifestElementsService} from './services/ManifestElementsService'
import {NativeActionsBuilder, CustomActionsBuilder} from './manifestBuilders/actions'
import type {DataModelAPI} from '../dataModel/dataModel'
import type {RefOverridesExtensionAPI} from '../refOverrides'
import {PresetsServiceFactory} from './presets/factory'
import {DataBuilderFactory} from './manifestBuilders/data'
import {LayoutBuilder} from './manifestBuilders/layout'
import {AppStudioRepository} from './repository/AppStudioRepository'
import type {DataModelExtensionAPI} from '../..'
import {ManifestDataService} from './services/ManifestDataService'
import {DisplayFiltersBuilder} from './manifestBuilders/displayFilters'

const {getPointer} = pointerUtils

export interface BuilderManifestOptions {
    typeMapper?(type: string, compPointer: Pointer): string | undefined
}

export interface BuilderManifestInfo {
    editorElement: Builder.Manifest['editorElement']
    devCenterEditorReactComponentId: string
}

export const generateWidgetSelector = (compIds: string[]) => {
    return compIds.map(compId => `.${compId}`).join(' ')
}

export const getCompManifestData = (createExtArgs: CreateExtArgs, compId: string) => {
    const {features} = createExtArgs.extensionAPI as FeaturesExtensionAPI
    return features.component.get(getPointer(compId, VIEW_MODES.DESKTOP), MANIFEST_FEATURE_NAME)
}

export const generateBuilderManifest = (
    createExtArgs: CreateExtArgs,
    widgetPageId: string,
    options?: BuilderManifestOptions
): Builder.Manifest => {
    const {pointers, dal} = createExtArgs
    const {
        features,
        nicknames,
        dataModel,
        variants: variantsAPI,
        refOverrides
    } = createExtArgs.extensionAPI as FeaturesExtensionAPI &
        NicknamesExtensionAPI &
        NicknameContextExtensionAPI &
        DataModelExtensionAPI &
        VariantsExtensionAPI &
        RefOverridesExtensionAPI

    const appStudioRepository = new AppStudioRepository(dal, dataModel)
    const pagePointer = pointers.structure.getPage(widgetPageId, VIEW_MODES.DESKTOP)
    const rootWidget = getRootAppWidgetByPage(createExtArgs, pagePointer)
    const widgetDescriptor = appStudioRepository.getWidgetDescriptorByPageId(widgetPageId)
    if (!rootWidget || !widgetDescriptor) {
        throw new Error(`missing data for widgetPageId: ${widgetPageId}`)
    }
    // Initialize repositories and services
    const componentRepository = new ComponentRepository(
        features as FeaturesAPI,
        nicknames as NicknamesAPI,
        dal,
        pointers
    )
    const widgetRepository = new WidgetRepository(pointers)
    const dataBuilderFactory = new DataBuilderFactory()

    const manifestDataService = new ManifestDataService(dataBuilderFactory, appStudioRepository)
    const refComponentRepository = new RefComponentRepository(refOverrides)
    const componentDataRepository = new ComponentDataRepository(
        createExtArgs.extensionAPI.dataModel as DataModelAPI,
        pointers
    )
    const manifestElementsService = new ManifestElementsService(
        widgetRepository,
        componentRepository,
        refComponentRepository,
        componentDataRepository,
        createExtArgs.coreConfig.experimentInstance,
        dataModel,
        options?.typeMapper
    )
    // Collect manifest information
    const manifestElements = manifestElementsService.getManifestElements(rootWidget)
    const manifestRootData = manifestDataService.getManifestRootData(createExtArgs, widgetPageId)
    const manifestData = getCompManifestData(createExtArgs, rootWidget.id)
    const componentType = componentRepository.getComponentType(rootWidget)

    const nativeActions = new NativeActionsBuilder(componentType, manifestData?.gfpp, dataModel).build()
    const customActions = new CustomActionsBuilder(componentType, manifestData?.gfpp, dataModel).build()

    const displayFilters = new DisplayFiltersBuilder(manifestData?.gfpp, componentType, manifestRootData).build()

    const layoutManifest = new LayoutBuilder(componentRepository).buildRootComponentLayoutManifest(rootWidget)

    const variableService = VariablesServiceFactory.createVariablesService(variantsAPI, rootWidget)
    const cssCustomProperties = variableService.getStylesManifest()

    const presetsService = PresetsServiceFactory.createPresetsService(createExtArgs, rootWidget)
    const presets = presetsService.getPresetsManifest(widgetDescriptor.presets.map(preset => preset.id))

    const editorElement = new EditorElementBuilder()
        .setDisplayName(manifestData?.displayName?.value ?? widgetDescriptor.name)
        .setCssCustomProperties(cssCustomProperties)
        .setElements(manifestElements)
        .setData(manifestRootData)
        .setActions(nativeActions)
        .setCustomActions(customActions)
        .setDisplayFilters(displayFilters)
        .setLayout(layoutManifest)
        .setSelector(generateWidgetSelector([rootWidget.id]))
        .setPresets(presets)
        .build()

    const manifestBuilder = new ComponentManifestBuilder()
    return manifestBuilder.setEditorElement(editorElement).build()
}

export const getBlocksBuilderManifests = (createExtArgs: CreateExtArgs): Record<string, BuilderManifestInfo> => {
    const {dal} = createExtArgs
    const {dataModel: dataModelAPI} = createExtArgs.extensionAPI as DataModelExtensionAPI
    const appStudioRepository = new AppStudioRepository(dal, dataModelAPI)
    const widgetsDescriptors = appStudioRepository.getWidgetsDescriptors()
    return widgetsDescriptors.reduce<Record<string, BuilderManifestInfo>>((acc, widgetDescriptor) => {
        const widgetPageId = widgetDescriptor.rootCompId.replace('#', '')
        const manifest = generateBuilderManifest(createExtArgs, widgetPageId)
        acc[widgetPageId] = {
            devCenterEditorReactComponentId: widgetDescriptor.devCenterEditorReactComponentId,
            editorElement: manifest.editorElement
        }
        return acc
    }, {})
}
